import assertNever from "assert-never";
import { IDisposable, Range } from "monaco-editor-core";
import * as monaco from "monaco-editor-core";

import { ColorTheme } from "../ColorTheme";
import { Vec2 } from "../Data/Geometry/Vec2";
import { Panic } from "../Errors/Panic";
import { BufferChange } from "../gen/Buffer";
import { EditorFile, EditorFileId, EditorFileVersion, LoadFileError } from "../gen/ClientState";
import { CodeDiagnostic } from "../gen/CodeDiagnostics";
import { PathRelFile } from "../gen/Path";
import { SyntaxHighlightingColor, SyntaxHighlightingSpan } from "../gen/SyntaxHighlighting";
import { LoadFileErrorPanelContainer } from "../LoadFileErrorPanelContainer";
import styles from "./EditorPanel.module.css";
import {
  CodeDiagnosticHighlightsDecorator,
  CodeDiagnosticSpan
} from "./EditorPanel/CodeDiagnosticHighlightsDecorator";
import { IEditorPanel } from "./IEditorPanel";

export class EditorPanel implements IEditorPanel, IDisposable {
  constructor(
    root: HTMLElement,
    initialEditors: EditorFile[],
    activeEditor: EditorFileId | null,
    private events: IEditorPanel.Events
  ) {
    this.loadFileErrorPanelContainer = new LoadFileErrorPanelContainer(root);

    this.editorInstance = createMonacoEditor(root);

    this.codeDiagnosticHighlightsDecorator = new CodeDiagnosticHighlightsDecorator(
      this.editorInstance
    );

    for (const editor of initialEditors) {
      this.addEditor(editor);
    }

    this.editorInstance.onMouseMove((e) => {
      // console.log(e.target.position);
    });

    this.editorInstance.onDidChangeCursorPosition((e) => {
      this.events.onCursorPositionChange({
        line: e.position.lineNumber,
        col: e.position.column
      });
    });

    this.editorInstance.onContextMenu((e) => {
      e.event.preventDefault();

      if (this.activeEditor === null || e.target.position === null) {
        return;
      }

      const lineCol = {
        line: e.target.position.lineNumber,
        col: e.target.position.column
      };
      const clickPos: Vec2 = {
        x: e.event.posx,
        y: e.event.posy
      };

      if (this.activeEditor.filePath !== null) {
        this.events.onEditorContextPopup(this.activeEditor.filePath, lineCol, clickPos);
      } else {
        // TODO ???
      }
    });

    this.editorInstance.onDidBlurEditorText(() => {
      // monaco.editor.setTheme("vs-dark");
    });

    this.editorInstance.onDidFocusEditorText(() => {
      // monaco.editor.setTheme("vs");
    });

    if (activeEditor === null) {
      this.editorInstance.setModel(null);
    } else {
      const editor = this.editors.get(activeEditor);
      if (editor === undefined) {
        throw new Panic(`Editor not found`, { activeEditor });
      }

      switch (editor.data.kind) {
        case "MonacoModal":
          this.editorInstance.setModel(editor.data.model);
          this.editorInstance.focus();
          break;
        case "LoadFileErrorData":
          this.editorInstance.setModel(null);
          this.loadFileErrorPanelContainer.showPanel(editor.filePath, editor.data.loadFileError);
          break;
        default:
          assertNever(editor.data);
      }

      this.activeEditor = editor;
    }
  }

  setColorTheme(colorTheme: ColorTheme): void {
    switch (colorTheme) {
      case "light":
        monaco.editor.setTheme("vs");
        break;
      case "dark":
        monaco.editor.setTheme("vs-dark");
        break;
      default:
        assertNever(colorTheme);
    }
  }

  private loadFileErrorPanelContainer: LoadFileErrorPanelContainer;
  private codeDiagnosticHighlightsDecorator: CodeDiagnosticHighlightsDecorator;

  dispose(): void {
    for (const editor of this.editors.values()) {
      disposeEditorState(editor);
    }
    this.editorInstance.dispose();
  }

  activateEditor(editorFileId: EditorFileId): void {
    const editor = this.editors.get(editorFileId);
    if (editor === undefined) {
      throw new Panic(`Editor not found`, { editorFileId });
    }

    if (this.activeEditor !== null) {
      switch (this.activeEditor.data.kind) {
        case "MonacoModal":
          this.activeEditor.data.viewState = this.editorInstance.saveViewState();
          break;
        case "LoadFileErrorData":
          this.loadFileErrorPanelContainer.hide();
          break;
        default:
          assertNever(this.activeEditor.data);
      }
    }

    switch (editor.data.kind) {
      case "MonacoModal": {
        this.editorInstance.setModel(editor.data.model);
        if (editor.data.viewState !== null) {
          this.editorInstance.restoreViewState(editor.data.viewState);
        }

        const position = this.editorInstance.getPosition();
        this.events.onCursorPositionChange(
          position === null
            ? {
                line: 1,
                col: 1
              }
            : {
                line: position.lineNumber,
                col: position.column
              }
        );

        this.editorInstance.focus();
        break;
      }
      case "LoadFileErrorData":
        this.editorInstance.setModel(null);
        this.loadFileErrorPanelContainer.showPanel(editor.filePath, editor.data.loadFileError);
        break;
      default:
        assertNever(editor.data);
    }

    this.activeEditor = editor;
  }

  performUndo(): void {
    this.editorInstance.trigger("editor", "undo", undefined);
  }

  performRedo(): void {
    this.editorInstance.trigger("editor", "redo", undefined);
  }

  performCut(): void {
    this.editorInstance.focus();
    this.editorInstance.trigger("editor", "editor.action.clipboardCutAction", undefined);
  }

  performCopy(): void {
    this.editorInstance.focus();
    this.editorInstance.trigger("editor", "editor.action.clipboardCopyAction", undefined);
  }

  performPaste(): void {
    this.editorInstance.focus();
    this.editorInstance.trigger("editor", "editor.action.clipboardPasteAction", undefined);
  }

  focus(): void {
    this.editorInstance.focus();
  }

  addEditor(editor: EditorFile): void {
    switch (editor.editorFileContents.kind) {
      case "textFile": {
        const model = monaco.editor.createModel(editor.editorFileContents.value.contents);

        if (editor.editorFileContents.value.syntaxHighlightingSpans !== null) {
          model.deltaDecorations(
            [],
            editor.editorFileContents.value.syntaxHighlightingSpans.map((span) => ({
              range: new Range(
                span.range.start.line,
                span.range.start.col,
                span.range.end.line,
                span.range.end.col
              ),
              options: {
                inlineClassName: colorToCssClass(span.color)
              }
            }))
          );
        }

        const monacoModel: EditorStateData.MonacoModel = {
          kind: "MonacoModal",
          model: model,
          viewState: null,
          registeredEvents: []
        };

        const editorState: EditorState = {
          editorFileId: editor.id,
          filePath: editor.filePath,
          data: monacoModel
        };

        this.editors.set(editor.id, editorState);
        monacoModelRegisterEvent(
          monacoModel,
          model.onDidChangeContent((e) => {
            // TODO This might need to be done a bit more cleanly.
            this.codeDiagnosticHighlightsDecorator.clear();

            this.events.onBufferChange(
              editor.id,
              e.changes.map<BufferChange>((c) => ({
                offset: c.rangeOffset,
                length: c.rangeLength,
                text: c.text
              }))
            );
          })
        );
        break;
      }
      case "loadFileError":
        this.editors.set(editor.id, {
          editorFileId: editor.id,
          filePath: editor.filePath,
          data: {
            kind: "LoadFileErrorData",
            loadFileError: editor.editorFileContents.value
          }
        });
        break;
      default:
        assertNever(editor.editorFileContents);
    }
  }

  closeEditor(editorFileId: EditorFileId): void {
    const editor = this.editors.get(editorFileId);
    if (editor === undefined) {
      throw new Panic(`Editor not found`, { editorFileId });
    }

    if (this.activeEditor === editor) {
      switch (this.activeEditor.data.kind) {
        case "MonacoModal":
          this.editorInstance.setModel(null);
          break;
        case "LoadFileErrorData":
          this.loadFileErrorPanelContainer.hide();
          break;
        default:
          assertNever(this.activeEditor.data);
      }
    }

    disposeEditorState(editor);
    this.editors.delete(editorFileId);
  }

  setSyntaxHighlighting(
    editorFileId: EditorFileId,
    version: EditorFileVersion,
    spans: SyntaxHighlightingSpan[]
  ): void {
    const editor = this.editors.get(editorFileId);
    if (editor === undefined) {
      throw new Panic(`Editor not found`, { editorFileId });
    }

    if (editor.data.kind !== "MonacoModal") {
      throw new Panic(`Editor is not of kind "MonacoModal"`, {
        editorFileId,
        kind: editor.data.kind
      });
    }

    editor.data.model.deltaDecorations(
      editor.data.model.getAllDecorations().map((d) => d.id),
      []
    );

    editor.data.model.deltaDecorations(
      [],
      spans.map((span) => ({
        range: new Range(
          span.range.start.line,
          span.range.start.col,
          span.range.end.line,
          span.range.end.col
        ),
        options: {
          inlineClassName: colorToCssClass(span.color)
        }
      }))
    );
  }

  setCodeDiagnostics(codeDiagnostics: CodeDiagnostic[]): void {
    const codeDiagnosticSpans: CodeDiagnosticSpan[] = [];
    for (const codeDiagnostic of codeDiagnostics) {
      // TODO !!! Only use codeDiagnostics from the current file.
      //
      // TODO2 !!! When we switch tabs we need to call
      // "codeDiagnosticHighlightsDecorator.setCodeDiagnosticSpans"
      if (codeDiagnostic.srcSpan !== null) {
        codeDiagnosticSpans.push({
          severity: codeDiagnostic.severity,
          srcSpan: codeDiagnostic.srcSpan
        });
      }
    }
    this.codeDiagnosticHighlightsDecorator.setCodeDiagnosticSpans(codeDiagnosticSpans);
  }

  private lookupFilePath(filePath: PathRelFile): EditorState | null {
    for (const model of this.editors.values()) {
      if (model.filePath === filePath) {
        return model;
      }
    }

    return null;
  }

  private editorInstance: monaco.editor.IStandaloneCodeEditor;
  private editors = new Map<EditorFileId, EditorState>();
  private activeEditor: EditorState | null = null;
}

const nullState: monaco.languages.IState = {
  clone: (): monaco.languages.IState => {
    return nullState;
  },
  equals: (other: monaco.languages.IState): boolean => {
    return other === nullState;
  }
};

interface EditorState {
  editorFileId: EditorFileId;
  filePath: PathRelFile | null;
  data: EditorStateData;
}

type EditorStateData = EditorStateData.MonacoModel | EditorStateData.LoadFileErrorData;

declare namespace EditorStateData {
  export interface MonacoModel {
    kind: "MonacoModal";
    model: monaco.editor.ITextModel;
    viewState: monaco.editor.ICodeEditorViewState | null;
    registeredEvents: IDisposable[];
  }

  export interface LoadFileErrorData {
    kind: "LoadFileErrorData";
    loadFileError: LoadFileError;
  }
}

function disposeEditorState(editorState: EditorState): void {
  switch (editorState.data.kind) {
    case "LoadFileErrorData":
      // Nothing to do
      break;
    case "MonacoModal":
      for (const registeredEvent of editorState.data.registeredEvents) {
        registeredEvent.dispose();
      }
      editorState.data.model.dispose();
      break;
    default:
      assertNever(editorState.data);
  }
}

function monacoModelRegisterEvent(
  monacoModel: EditorStateData.MonacoModel,
  event: IDisposable
): void {
  monacoModel.registeredEvents.push(event);
}

function createMonacoEditor(parent: HTMLElement): monaco.editor.IStandaloneCodeEditor {
  // "Is there a way to disable IntelliSense?" <https://github.com/microsoft/monaco-editor/issues/1681>
  const editorInstance = monaco.editor.create(parent, {
    matchBrackets: "never",
    selectionHighlight: false,
    tabSize: 2,
    automaticLayout: true,
    theme: "vs",
    scrollbar: {
      verticalHasArrows: true,
      useShadows: false,
      arrowSize: 15,
      horizontalScrollbarSize: 15,
      verticalScrollbarSize: 15
    },
    detectIndentation: false,
    autoClosingBrackets: "never",
    quickSuggestions: false,
    snippetSuggestions: "none",
    occurrencesHighlight: false,
    contextmenu: false,
    autoIndent: "keep",
    suggest: {
      showStatusBar: false,
      showSnippets: false
    },
    dragAndDrop: false,
    suggestOnTriggerCharacters: false,
    minimap: {
      // showSlider: "always"
    }
  });
  // let contents = "";
  // for (let i = 0; i < 5000; ++i) {
  //   contents += `This is a line of text ${i}\n`;
  // }
  // x.disp
  // x.getModel()!.setValue("Hello\nWorld\nThis is the initial value\n" + contents);
  // x.onDidChangeModelContent((e) => {
  //   console.log(e);
  // });
  // let decs: editor.IModelDeltaDecoration[] = [];
  // for (let i = 0; i < 4000; ++i) {
  //   decs.push({
  //     range: new Range(i + 10, 2, i + 10, 10),
  //     options: {
  //       inlineClassName: styles.test_highlight1,
  //       minimap: {
  //         color: "#0ff",
  //         position: editor.MinimapPosition.Inline
  //       }
  //     }
  //   });
  // }

  // x.deltaDecorations([], decs.concat([
  //   {
  //     range: new Range(1, 2, 2, 4),
  //     options: {
  //       inlineClassName: styles.test_highlight1
  //     }
  //   },
  //   {
  //     range: new Range(1, 4, 2, 4),
  //     options: { inlineClassName: styles.test_highlight2 },
  //   }
  // ]));
  // editor.setModelMarkers(x.getModel()!, "owner", [
  //   {
  //     startLineNumber: 2,
  //     startColumn: 17,
  //     endLineNumber: 2,
  //     endColumn: 24,
  //     message: 'Warning!',
  //     severity: MarkerSeverity.Warning
  //   },
  //   {
  //     startLineNumber: 3,
  //     startColumn: 18,
  //     endLineNumber: 3,
  //     endColumn: 23,
  //     message: 'Erorr!',
  //     severity: MarkerSeverity.Error
  //   }
  // ]);

  return editorInstance;
}

function colorToCssClass(color: SyntaxHighlightingColor): string {
  switch (color) {
    case SyntaxHighlightingColor.binding:
      return styles.syntax_binding;
    case SyntaxHighlightingColor.comment:
      return styles.syntax_comment;
    case SyntaxHighlightingColor.import_:
      return styles.syntax_import;
    case SyntaxHighlightingColor.keyword:
      return styles.syntax_keyword;
    case SyntaxHighlightingColor.lambda:
      return styles.syntax_lambda;
    case SyntaxHighlightingColor.list:
      return styles.syntax_list;
    case SyntaxHighlightingColor.module_:
      return styles.syntax_module;
    case SyntaxHighlightingColor.numericLiteral:
      return styles.syntax_numericLiteral;
    case SyntaxHighlightingColor.operator:
      return styles.syntax_operator;
    case SyntaxHighlightingColor.patVar:
      return styles.syntax_patVar;
    case SyntaxHighlightingColor.stringLiteral:
      return styles.syntax_stringLiteral;
    case SyntaxHighlightingColor.tuple:
      return styles.syntax_tuple;
    case SyntaxHighlightingColor.typeClass:
      return styles.syntax_typeClass;
    case SyntaxHighlightingColor.typeCon:
      return styles.syntax_typeCon;
    case SyntaxHighlightingColor.typeVar:
      return styles.syntax_typeVar;
    case SyntaxHighlightingColor.variable:
      return styles.syntax_variable;
    default:
      return assertNever(color);
  }
}
